import type { Tone } from '../core/types.js';

export type ToneKey =
  | 'success'
  | 'stretch'
  | 'near_miss'
  | 'early_slip'
  | 'urge_logged'
  | 'urge_passed'
  | 'urge_beaten'
  | 'rescue_complete'
  | 'rescue_abandon'
  | 'wait_acknowledged'
  | 'waited_but_used'
  | 'welcome_back'
  | 'low_data'
  | 'bad_week'
  | 'money_protected'
  | 'plan_easier'
  | 'plan_harder'
  | 'plan_paused'
  | 'plan_resumed'
  | 'exception_logged'
  | 'safety_general'
  | 'safety_alcohol'
  | 'safety_gambling'
  | 'safety_crisis'
  | 'challenge_offer'
  | 'challenge_completed'
  | 'challenge_not_met'
  | 'challenge_regenerated'
  | 'target_reached_in_module';

const CATALOG: Record<ToneKey, Record<Tone, string>> = {
  success: {
    warm: 'You did the wait. That was real effort.',
    calm: 'Target reached. Logged.',
    playful: 'Nice — you stretched the gap.',
    direct: 'Target hit. Next gap goes up.',
    minimal: 'Target hit.',
  },
  stretch: {
    warm: 'You held on past the target. Beautiful.',
    calm: 'Stretch success. Plan grew a touch more.',
    playful: 'Bonus round! Bigger jump this time.',
    direct: 'Past target. Larger growth applied.',
    minimal: 'Stretch success.',
  },
  near_miss: {
    warm: 'So close. The next one stays the same so you can land it.',
    calm: 'Near miss. Holding the target.',
    playful: 'A whisker short. Same target, take two.',
    direct: 'Just below target. No change.',
    minimal: 'Near miss.',
  },
  early_slip: {
    warm: 'No shame. The next target is a little easier.',
    calm: 'Early use. Plan eased to keep momentum.',
    playful: 'Speed bump. We adjust and keep moving.',
    direct: 'Used early. Plan eased.',
    minimal: 'Early use. Plan eased.',
  },
  urge_logged: {
    warm: 'Noticing the urge is part of the work.',
    calm: 'Urge logged.',
    playful: 'Spotted it. Half the battle.',
    direct: 'Urge recorded.',
    minimal: 'Urge logged.',
  },
  urge_passed: {
    warm: 'You rode that wave. It passed.',
    calm: 'Urge passed.',
    playful: 'Surf, dismount, repeat.',
    direct: 'Urge passed.',
    minimal: 'Urge passed.',
  },
  urge_beaten: {
    warm: "You beat that one. Hold the win.",
    calm: 'Urge beaten.',
    playful: 'Crushed it.',
    direct: 'Urge beaten.',
    minimal: 'Urge beaten.',
  },
  rescue_complete: {
    warm: 'You stayed with it. That bought you space.',
    calm: 'Session complete.',
    playful: 'Boom — autopilot interrupted.',
    direct: 'Rescue complete.',
    minimal: 'Rescue complete.',
  },
  rescue_abandon: {
    warm: 'Showing up matters more than finishing. Try a shorter one next.',
    calm: 'Session ended early.',
    playful: 'No worries — speedrun the short one next time.',
    direct: 'Rescue abandoned.',
    minimal: 'Rescue ended early.',
  },
  wait_acknowledged: {
    warm: 'Nice — you waited. Counts.',
    calm: 'Wait logged.',
    playful: 'Patience: 1, autopilot: 0.',
    direct: 'Wait recorded.',
    minimal: 'Wait logged.',
  },
  waited_but_used: {
    warm: 'You waited first. That still interrupted the loop.',
    calm: 'Use logged. Wait recorded.',
    playful: 'Bought yourself time. Still progress.',
    direct: 'Used after wait. Logged.',
    minimal: 'Used after wait.',
  },
  welcome_back: {
    warm: 'Welcome back. No need to catch up perfectly. Start with today.',
    calm: "Welcome back. Start where you are.",
    playful: 'Hey, stranger. Just pick up where you are.',
    direct: 'Welcome back. Continue plan or adjust.',
    minimal: 'Welcome back.',
  },
  low_data: {
    warm: 'Your first few logs teach Wean your pattern. Messy data still helps.',
    calm: 'Not enough data yet. Keep logging.',
    playful: 'Need more dots to connect. Keep going.',
    direct: 'Insufficient data for insights.',
    minimal: 'Not enough data yet.',
  },
  bad_week: {
    warm: 'This week was hard. The useful question is what we can protect next.',
    calm: 'A tougher week. Patterns are still data.',
    playful: "Rough patch — we tune and reroll.",
    direct: 'Use count up vs prior week. Consider easing the target.',
    minimal: 'Tougher week vs last.',
  },
  money_protected: {
    warm: 'Nice pause — you protected real money.',
    calm: 'Money protected.',
    playful: 'Wallet wins.',
    direct: 'Skipped purchase logged.',
    minimal: 'Money protected.',
  },
  plan_easier: {
    warm: 'Slowing down can keep momentum. Easier this week is fine.',
    calm: 'Plan eased.',
    playful: 'Lower difficulty — still leveling up.',
    direct: 'Target lowered.',
    minimal: 'Plan eased.',
  },
  plan_harder: {
    warm: 'You handled this. Ready for a small stretch?',
    calm: 'Plan stretched.',
    playful: 'Crank up the dial.',
    direct: 'Target raised.',
    minimal: 'Plan stretched.',
  },
  plan_paused: {
    warm: 'A pause is not quitting. It keeps the plan honest.',
    calm: 'Plan paused.',
    playful: 'Coffee break. Plan resumes when you do.',
    direct: 'Plan paused.',
    minimal: 'Plan paused.',
  },
  plan_resumed: {
    warm: 'Welcome back to the plan.',
    calm: 'Plan resumed.',
    playful: 'And we are back.',
    direct: 'Plan resumed.',
    minimal: 'Plan resumed.',
  },
  exception_logged: {
    warm: 'Marking an exception helps Wean understand the day.',
    calm: 'Exception logged.',
    playful: 'Day flagged. Carry on.',
    direct: 'Exception logged.',
    minimal: 'Exception logged.',
  },
  safety_general: {
    warm: 'Wean can support a reduction plan, but it is not medical treatment.',
    calm: 'Wean is a pacing tool, not a clinician.',
    playful: 'Pacing app, not a doctor.',
    direct: 'Wean is not medical treatment.',
    minimal: 'Not medical treatment.',
  },
  safety_alcohol: {
    warm: 'If you drink heavily or notice withdrawal symptoms, please talk to a clinician before reducing suddenly.',
    calm: 'Heavy daily drinking can be unsafe to stop suddenly — consult a clinician.',
    playful: 'Heads up: cold-turkey alcohol can be risky. See a clinician first.',
    direct: 'Heavy daily drinking: consult clinician before abrupt reduction.',
    minimal: 'See clinician before abrupt reduction.',
  },
  safety_gambling: {
    warm: 'Adding barriers can help: self-exclusion, deposit limits, bank blocks, or a trusted person.',
    calm: 'Consider barriers: self-exclusion, deposit limits, bank blocks, accountability.',
    playful: 'Barriers > willpower: self-exclusion, deposit limits, a friend.',
    direct: 'Add barriers: self-exclusion, deposit limits, bank blocks.',
    minimal: 'Consider self-exclusion + deposit limits.',
  },
  safety_crisis: {
    warm: 'If you might hurt yourself or someone else, please seek immediate help — emergency services or a crisis line.',
    calm: 'In a crisis, contact emergency services or a local crisis line.',
    playful: 'If things feel dangerous right now, call a crisis line — not the app.',
    direct: 'Crisis: contact emergency services or a crisis line.',
    minimal: 'Crisis: call emergency services.',
  },
  challenge_offer: {
    warm: 'One more challenge first — it usually buys you a few more minutes.',
    calm: 'A short challenge can extend the wait. Want to try?',
    playful: 'One more round? Future-you usually says thanks.',
    direct: 'One more challenge before deciding.',
    minimal: 'One challenge first.',
  },
  challenge_completed: {
    warm: 'You did it. The urge had to wait a little longer — that\'s a win.',
    calm: 'Challenge complete. Wait extended.',
    playful: 'Nailed it. The urge stayed in line.',
    direct: 'Challenge met. Extra delay logged.',
    minimal: 'Challenge met.',
  },
  challenge_not_met: {
    warm: 'You stayed with it longer than you would have. That counts.',
    calm: 'Partial credit — every extra second mattered.',
    playful: 'Didn\'t clear it, but you bought time. Worth it.',
    direct: 'Not met. Delay still logged.',
    minimal: 'Not met. Delay logged.',
  },
  challenge_regenerated: {
    warm: 'Here\'s a different one. Pick whichever feels possible.',
    calm: 'New challenge.',
    playful: 'Swapped. Try this one.',
    direct: 'New challenge.',
    minimal: 'New.',
  },
  target_reached_in_module: {
    warm: 'You made the target while you were in there. Quietly proud of you.',
    calm: 'Target reached while in the rescue. Nice.',
    playful: 'Boom — target hit mid-rescue. Sneaky win.',
    direct: 'Target reached during rescue.',
    minimal: 'Target reached.',
  },
};

export function copy(key: ToneKey, tone: Tone): string {
  const entry = CATALOG[key];
  return entry[tone] ?? entry.calm;
}

export const TONE_OPTIONS: ReadonlyArray<{ value: Tone; label: string; sub: string }> = [
  { value: 'warm', label: 'Warm', sub: 'Encouraging and gentle.' },
  { value: 'calm', label: 'Calm', sub: 'Coach-like and steady.' },
  { value: 'playful', label: 'Playful', sub: 'Light, motivating.' },
  { value: 'direct', label: 'Direct', sub: 'Plain and grounded.' },
  { value: 'minimal', label: 'Minimal', sub: 'Almost no extra words.' },
];
